(() => {
  if (customElements.get('related-products-loader')) return;

  class RelatedProductsLoader extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async load() {
      if (this.loading || !this.dataset.url) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.abortController = new AbortController();

      try {
        const html = await this.fetchWithRetry(this.dataset.url);
        if (!this.isConnected) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const section = documentFragment.querySelector('[data-product-recommendations]');
        if (section) this.replaceWith(section);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Failed to load related products:', error);
        }
      } finally {
        if (this.isConnected) this.removeAttribute('aria-busy');
        this.loading = false;
      }
    }

    async fetchWithRetry(url) {
      const delays = [0, 350, 900];
      let lastError;

      for (const delay of delays) {
        if (delay > 0) await this.wait(delay);
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: this.abortController.signal,
          });
          if (response.ok) return response.text();
          lastError = new Error(`Recommendation request failed: ${response.status}`);
          if (response.status !== 429 && response.status < 500) throw lastError;
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          lastError = error;
        }
      }

      throw lastError || new Error('Recommendation request failed.');
    }

    wait(delay) {
      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(resolve, delay);
        this.abortController.signal.addEventListener(
          'abort',
          () => {
            window.clearTimeout(timeout);
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true }
        );
      });
    }
  }

  customElements.define('related-products-loader', RelatedProductsLoader);

  if (!customElements.get('cart-related-products-loader')) {
    class CartRelatedProductsLoader extends RelatedProductsLoader {
      async load() {
        if (this.loading || !this.dataset.url) return;
        this.loading = true;
        this.setAttribute('aria-busy', 'true');
        this.abortController = new AbortController();

        try {
          const html = await this.fetchWithRetry(this.dataset.url);
          if (!this.isConnected) return;

          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const recommendations = documentFragment.querySelector('[data-cart-related-page-cards]');
          if (recommendations) {
            this.replaceWith(recommendations);
            return;
          }

          const pairingShell = this.closest('[data-cart-pairing-page-shell]');
          this.remove();
          if (pairingShell && !pairingShell.querySelector('[data-cart-pairing-section]')) {
            pairingShell.remove();
          }
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.error('Failed to load cart related products:', error);
          }
          if (this.isConnected) {
            const pairingShell = this.closest('[data-cart-pairing-page-shell]');
            this.remove();
            if (pairingShell && !pairingShell.querySelector('[data-cart-pairing-section]')) {
              pairingShell.remove();
            }
          }
        } finally {
          if (this.isConnected) this.removeAttribute('aria-busy');
          this.loading = false;
        }
      }
    }

    customElements.define('cart-related-products-loader', CartRelatedProductsLoader);
  }
})();
